import { useMemo } from 'react'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { PaginationPaging } from 'types/pagination'

/**
 * Calculates the number of items returned in the next page.
 * @param pagination - The current pagination state.
 * @param totalItems - The total number of items.
 * @returns The number of items in the next page.
 */
const calculateNextPageItems = (pagination: PaginationPaging, totalItems: number): number => {
  const { page, max } = pagination
  const currentPage = page + 1

  const currentItemsViewed = currentPage * max

  if (totalItems - currentItemsViewed < max) {
    return totalItems - currentItemsViewed
  }

  return max
}

/**
 * Hook that calculates the number of items returned in the next page.
 * @param paging - The current pagination state.
 * @returns The number of items in the next page.
 */
export const usePaginationNextPageItemCount = (paging: PaginationPaging) => {
  return useMemo(() => {
    if (!paging) {
      return paging?.max
    }

    const nextPageItems = calculateNextPageItems(paging, paging?.total)

    if (!nextPageItems || nextPageItems < 0) {
      return MIN_PAGINATION_SIZE
    }

    return nextPageItems
  }, [paging])
}
